// src/components/AdminLogin.jsx
// ------------------------------------------------------------
//  Server login establishes the HttpOnly authentication cookie. The raw
//  administrator access code is retained only as an in-memory UI mode marker.
// ------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { useAssessmentContext } from '../contexts/AssessmentContext';
import { acceptCookieAuthentication, setAdminAccessCode } from '../api/client';
import { LogOut, Mail, Lock, Shield, AlertTriangle } from 'lucide-react';
import AppFooter from './AppFooter';

export default function AdminLogin({ onLogin, userRole }) {
  /* ---------------------------------- state --------------------------------- */
  const { loginUser } = useAdmin();
  const { accessCode, setAccessCode } = useAssessmentContext();

  const [email,       setEmail]       = useState('');
  const [pin,         setPin]         = useState('');
  const [adminAccess, setAdminAccess] = useState('');
  const [error,       setError]       = useState('');
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);

  /* ---------------------------- responsive resize --------------------------- */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ---------------------------- dynamic CSS once --------------------------- */
  useEffect(() => {
    if (document.getElementById('admin-login-styles')) return;

    const css = `@keyframes fadeIn{0%{opacity:0;transform:translateY(10px)}100%{opacity:1;transform:translateY(0)}}
    @keyframes pulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,.4)}70%{box-shadow:0 0 0 10px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}
    .admin-input:focus{border-color:#10b981!important;box-shadow:0 0 0 3px rgba(16,185,129,.25)!important;outline:none!important}
    .admin-button:hover{background-image:linear-gradient(to right,#0d9488,#059669)!important;transform:translateY(-2px)!important;box-shadow:0 4px 10px rgba(13,148,136,.3)!important}
    .exit-button:hover{background-color:#334155!important;transform:translateY(-1px)!important;box-shadow:0 4px 6px rgba(0,0,0,.1)!important}
    ::placeholder{color:rgba(148,163,184,.7)}`;

    const style = document.createElement('style');
    style.id = 'admin-login-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }, []);

  /* ------------------------------- handlers -------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      /* ---------------------------- ADMIN LOGIN ---------------------------- */
      if (userRole === 'admin') {
        const res = await fetch('/api/admin/login', {
          method : 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ email, pinCode: pin, accessCode: adminAccess })
        });

        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(res.status === 429
            ? (payload.error || 'Too many attempts. Please wait and try again.')
            : 'Invalid credentials');
        }

        const adminUser = await res.json();
        loginUser(adminUser);
        setAdminAccessCode(adminAccess);
        acceptCookieAuthentication();
        onLogin?.();
        return;
      }

      /* --------------------------- TEACHER LOGIN --------------------------- */
      if (!accessCode) throw new Error('Access-code verification is required');
      const res = await fetch('/api/users/login', {
        method : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ email, pinCode: pin, accessCode })
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(res.status === 429
          ? (payload.error || 'Too many attempts. Please wait and try again.')
          : 'Invalid credentials');
      }

      const teacher = await res.json();
      if (!['teacher', 'main_user'].includes(teacher.role)) {
        throw new Error('Invalid credentials');
      }

      loginUser(teacher);
      acceptCookieAuthentication();
      localStorage.setItem(
          'accessCode',
          teacher.accessCode || teacher.access_code
        );
      onLogin?.();
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    }
  };

  const handleExit = () => {
    setAccessCode('');
    window.location.reload();
  };

  /* --------------------------------- styles -------------------------------- */
  const container = {
    minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
    background:'linear-gradient(to bottom,#0f172a,#1e293b,#334155)',padding:isMobile?'1rem':'2rem',
    position:'relative',overflow:'hidden'
  };
  const blobWrap = { position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0 };
  const card = {
    width:'100%',maxWidth:'500px',background:'rgba(15,23,42,.8)',padding:isMobile?'1.5rem':'2.5rem',
    borderRadius:'1rem',boxShadow:'0 10px 25px rgba(0,0,0,.2),0 5px 10px rgba(0,0,0,.1)',marginBottom:'1.5rem',
    backdropFilter:'blur(12px)',border:'1px solid rgba(148,163,184,.2)',color:'#fff',position:'relative',zIndex:1,
    animation:'fadeIn .4s ease'
  };
  const title = {
    fontSize:isMobile?'1.75rem':'2rem',fontWeight:'bold',textAlign:'center',marginBottom:'1.5rem',color:'#e2e8f0',
    borderBottom:'2px solid rgba(148,163,184,.2)',paddingBottom:'1rem'
  };
  const formGroup = { marginBottom:'1.25rem' };
  const label = { display:'flex',alignItems:'center',gap:'.5rem',fontSize:'.95rem',fontWeight:500,marginBottom:'.5rem',color:'#94a3b8' };
  const input = {
    width:'100%',padding:'.875rem',fontSize:'1rem',border:'1px solid rgba(148,163,184,.3)',borderRadius:'.5rem',marginBottom:'.75rem',
    boxSizing:'border-box',background:'rgba(30,41,59,.5)',color:'#fff',transition:'all .2s ease'
  };
  const primaryBtn = {
    width:'100%',padding:'.875rem',fontSize:'1rem',fontWeight:600,border:'none',borderRadius:'.5rem',cursor:'pointer',
    background:'#10b981',backgroundImage:'linear-gradient(to right,#10b981,#0d9488)',color:'#fff',marginTop:'1.25rem',
    display:'flex',alignItems:'center',justifyContent:'center',gap:'.5rem',transition:'all .2s ease',boxShadow:'0 4px 10px rgba(16,185,129,.2)'
  };
  const exitBtn = {
    width:'100%',padding:'.875rem',fontSize:'1rem',fontWeight:600,border:'none',borderRadius:'.5rem',cursor:'pointer',
    background:'#475569',color:'#fff',marginTop:'.75rem',display:'flex',alignItems:'center',justifyContent:'center',gap:'.5rem',
    transition:'all .2s ease',boxShadow:'0 2px 4px rgba(0,0,0,.1)'
  };
  const errorBox = {
    padding:'.75rem',background:'rgba(153,27,27,.3)',borderRadius:'.5rem',color:'#fecaca',marginBottom:'1rem',textAlign:'center',
    display:'flex',alignItems:'center',justifyContent:'center',gap:'.5rem',border:'1px solid rgba(220,38,38,.3)'
  };

  /* ---------------------------------- JSX ---------------------------------- */
  return (
    <div style={container}>
      {/* Decorative blobs */}
      <div style={blobWrap}>
        <div style={{position:'absolute',top:'10%',left:'25%',width:'25rem',height:'25rem',background:'rgba(16,185,129,.07)',borderRadius:'50%',filter:'blur(70px)'}} />
        <div style={{position:'absolute',bottom:'20%',right:'25%',width:'20rem',height:'20rem',background:'rgba(13,148,136,.07)',borderRadius:'50%',filter:'blur(70px)'}} />
        <div style={{position:'absolute',top:'40%',right:'30%',width:'15rem',height:'15rem',background:'rgba(139,92,246,.05)',borderRadius:'50%',filter:'blur(70px)'}} />
      </div>

      <div style={card}>
        <h2 style={title}>{userRole === 'admin' ? 'Admin Login' : 'Teacher Login'}</h2>
        <form onSubmit={handleSubmit}>
          {/* --------------------------- email --------------------------- */}
          <div style={formGroup}>
            <label style={label}><Mail size={16}/>Email Address</label>
            <input type="email" placeholder="Enter your email address" value={email} onChange={e=>setEmail(e.target.value)} style={input} className="admin-input" maxLength={254} required />
          </div>
          {/* ----------------------------- pin --------------------------- */}
          <div style={formGroup}>
            <label style={label}><Lock size={16}/>PIN Code</label>
            <input type="password" placeholder="Enter your PIN code" value={pin} onChange={e=>setPin(e.target.value)} style={input} className="admin-input" maxLength={64} required />
          </div>
          {/* ---------------------- admin access code -------------------- */}
          {userRole === 'admin' && (
            <div style={formGroup}>
              <label style={label}><Shield size={16}/>Admin Access Code</label>
              <input type="password" placeholder="Enter admin access code" value={adminAccess} onChange={e=>setAdminAccess(e.target.value)} style={input} className="admin-input" maxLength={128} required />
            </div>
          )}
          {/* ----------------------------- error -------------------------- */}
          {error && (
            <div style={errorBox}><AlertTriangle size={16} color="#fecaca"/><span>{error}</span></div>
          )}

          <button type="submit" style={primaryBtn} className="admin-button"><Lock size={18}/>Sign in</button>
        </form>
        <button onClick={handleExit} style={exitBtn} className="exit-button"><LogOut size={18}/>Exit</button>
      </div>
      <AppFooter style={{ position: 'relative', zIndex: 1 }} />
    </div>
  );
}
